import {warn} from './warn';

export const REDACTED_REPLACE_TEXT = '[REDACTED]';

export interface RedactPattern {
  regex: RegExp;
  replaceText?: string;
}

export const PATTERNS = {
  sessionID: {
    regex: /(\/session\/(live|test))_(\w+)/g,
    replaceText: `$1_${REDACTED_REPLACE_TEXT}`,
  },
  // Pattern definitions: https://trailhead.corp.stripe.com/docs/privacy-guidance/patterns-of-sensitive-data
  emailAddresses: {
    regex:
      /[a-zA-Z0-9][a-zA-Z0-9._%+-]{0,62}[a-zA-Z0-9](?:@|%40|%2540)(?!stripe\.com)(?:[a-zA-Z0-9%-]{1,63}\.)+[a-zA-Z]{2,}/gi,
  },
  phoneNumbers: {
    regex:
      /(?:\+\d{1,3}\s\d{1,4}\s\d{3,4}\s\d{4}|\+\d{1,3}\s\(\d{3}\)\s\d{3}\s\d{4}|\+\d{1,3}\s\d{3}\s\d{3}\s\d{4}|\(\d{3}\)\s?\d{3}-\d{4}|\(\d{3}\)-\d{3}-\d{4}|\d{3}-\d{3}-\d{4}|\d{3}\.\d{3}\.\d{4}|\d{3}\s\d{3}\s\d{4})\b/gi,
  },
  creditCardNumbers: {
    regex:
      /\b(?:\d{4}[\s\-._]){3}\d{4}|\d{4}[\s\-._]\d{6}[\s\-._]\d{4}|\d{4}[\s\-._]\d{6}[\s\-._]\d{5}|\d{4}[\s\-._]\d{4}[\s\-._]\d{4}[\s\-._]\d{2}\b/gi,
  },
  apiKeysWithLivemode: {
    regex:
      /(key|keyinfo|cap|authtokensignsession|osk|sk_org|sk|rk|sys|cpt|pt|ps|em|apk|ark|pss|pst)_(live|test)_([A-Za-z0-9]+)/gi,
    replaceText: `$1_$2_${REDACTED_REPLACE_TEXT}`,
  },
  secretKeysWithoutLivemode: {
    regex: /(sk|sys)_([A-Za-z0-9]{29})/gi,
    replaceText: `$1_${REDACTED_REPLACE_TEXT}`,
  },
  ephemeralSessionKeys: {
    regex:
      /(ek|pst|pss)_(live|test)_([A-Za-z0-9]{71}|[A-Za-z0-9]{76}|[A-Za-z0-9]{86}|[A-Za-z0-9]{92})_([A-Za-z0-9]{10})/gi,
    replaceText: `$1_$2_${REDACTED_REPLACE_TEXT}_${REDACTED_REPLACE_TEXT}`,
  },
  linkSessionKeys: {
    regex: /(lsk)_([A-Za-z0-9]+)/gi,
    replaceText: `$1_${REDACTED_REPLACE_TEXT}`,
  },
  sessionTokensInCookies: {
    regex:
      /(snc)_(mble|dash|site|sprt|mrkt|conv|docs|nxds|scli|accs)_([A-Za-z0-9+/=]{76})_([A-Za-z0-9+/=]{4})_([A-Za-z0-9+/=]{10})/gi,
    replaceText: `$1_$2_${REDACTED_REPLACE_TEXT}_${REDACTED_REPLACE_TEXT}_${REDACTED_REPLACE_TEXT}`,
  },
  userInviteSecret: {
    regex: /(uis)_([a-zA-Z0-9]{32})/gi,
    replaceText: `$1_${REDACTED_REPLACE_TEXT}`,
  },
  awsAccessKeys: {
    regex: /(AKIA)[0-9A-Z]{16}/gi,
    replaceText: `$1${REDACTED_REPLACE_TEXT}`,
  },
  gitHubOAuthKeys: {
    regex: /(gh[opsur])_([0-9a-zA-Z]{36})/gi,
    replaceText: `$1_${REDACTED_REPLACE_TEXT}`,
  },
  slackAppLevelTokens: {
    regex: /(xapp)-\d-[A-Z0-9]+-\d+-[a-z0-9]+/gi,
    replaceText: `$1_${REDACTED_REPLACE_TEXT}`,
  },
  slackBotTokens: {
    regex: /(xoxb)-[0-9]{10,13}-[0-9]{10,13}[a-zA-Z0-9-]*/gi,
    replaceText: `$1_${REDACTED_REPLACE_TEXT}`,
  },
  sourcegraphAccessToken: {
    regex: /(sgp)_([A-Za-z0-9]+)/gi,
    replaceText: `$1_${REDACTED_REPLACE_TEXT}`,
  },
  twilioApiKeys: {
    regex: /(SK)[0-9a-fA-F]{32}/gi,
    replaceText: `$1${REDACTED_REPLACE_TEXT}`,
  },
  slackWebhookUrl: {
    regex:
      /(https:\/\/hooks\.slack\.com\/services\/)T[a-zA-Z0-9_]{8,10}\/B[a-zA-Z0-9_]{8,10}\/[a-zA-Z0-9_]{24}/gi,
    replaceText: `$1${REDACTED_REPLACE_TEXT}`,
  },
} as const;

const PATTERNS_RECORD = PATTERNS as Record<string, RedactPattern>;

export const redactPII = (value: string): string => {
  let redactedValue = value;

  Object.keys(PATTERNS_RECORD).forEach((pattern) => {
    const {regex, replaceText} = PATTERNS_RECORD[pattern];

    try {
      // Reset lastIndex for global regex to ensure consistent behavior
      regex.lastIndex = 0;

      redactedValue = redactedValue.replaceAll(
        regex,
        replaceText || REDACTED_REPLACE_TEXT,
      );
    } catch (error) {
      warn('redaction_pattern_failed', {pattern, error});
    }
  });

  return redactedValue;
};
